if (!customElements.get('byte-collection-description')) {
  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ByteCollectionDescription extends HTMLElement {
    #content = null;
    #toggle = null;
    #label = null;
    #boundResize = null;
    #boundToggle = null;
    #expanded = false;
    #needsClamp = false;
    #animating = false;

    connectedCallback() {
      this.#content = this.querySelector('[data-collection-desc-content]');
      this.#toggle = this.querySelector('[data-collection-desc-toggle]');
      this.#label = this.querySelector('[data-collection-desc-label]');

      if (!this.#content || !this.#toggle || !this.#label) return;

      this.#boundToggle = (e) => {
        e.preventDefault();
        if (this.#animating) return;

        if (prefersReducedMotion()) {
          this.#expanded = !this.#expanded;
          this.#syncLayout(true);
          return;
        }

        if (this.#expanded) {
          this.#animateCollapse();
        } else {
          this.#animateExpand();
        }
      };
      this.#toggle.addEventListener('click', this.#boundToggle);

      this.#boundResize = () => {
        if (this.#animating) return;
        this.#syncLayout(true);
      };
      window.addEventListener('resize', this.#boundResize);

      this.#syncLayout(true);
    }

    disconnectedCallback() {
      this.#toggle?.removeEventListener('click', this.#boundToggle);
      window.removeEventListener('resize', this.#boundResize);
    }

    #collapsedHeightPx() {
      const raw = getComputedStyle(this).getPropertyValue('--byte-collection-desc-collapsed').trim();
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 200;
    }

    #syncLayout(instant) {
      if (!this.#content || !this.#toggle) return;

      const max = this.#collapsedHeightPx();
      this.#needsClamp = this.#content.scrollHeight > max + 1;

      if (!this.#needsClamp) {
        this.#toggle.hidden = true;
        this.#expanded = false;
        this.classList.add('is-no-clamp');
        this.classList.remove('is-enhanced', 'is-expanded');
        this.#toggle.removeAttribute('aria-expanded');
        this.#content.style.maxHeight = '';
        this.#content.style.overflow = '';
        return;
      }

      this.classList.remove('is-no-clamp');
      this.classList.add('is-enhanced');
      this.#toggle.hidden = false;

      if (this.#expanded) {
        this.classList.add('is-expanded');
        this.#content.style.overflow = 'visible';
        this.#content.style.maxHeight = 'none';
        this.#toggle.setAttribute('aria-expanded', 'true');
        this.#label.textContent = this.#toggle.dataset.labelLess || '';
      } else {
        this.classList.remove('is-expanded');
        this.#applyCollapsedStyles(instant);
        this.#toggle.setAttribute('aria-expanded', 'false');
        this.#label.textContent = this.#toggle.dataset.labelMore || '';
      }
    }

    #applyCollapsedStyles(instant) {
      if (!this.#content) return;
      const px = this.#collapsedHeightPx();

      if (instant) {
        this.#content.style.transition = 'none';
      }

      this.#content.style.overflow = 'hidden';
      this.#content.style.maxHeight = `${px}px`;

      if (instant) {
        void this.#content.offsetHeight;
        this.#content.style.transition = '';
      }
    }

    #animateExpand() {
      if (!this.#content || !this.#needsClamp) return;

      const collapsed = this.#collapsedHeightPx();
      const full = this.#content.scrollHeight;

      this.#animating = true;
      this.classList.add('is-animating-expand');

      this.#content.style.overflow = 'hidden';
      this.#content.style.maxHeight = `${collapsed}px`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#content.style.maxHeight = `${full}px`;
        });
      });

      this.#content.addEventListener(
        'transitionend',
        (e) => {
          if (e.target !== this.#content || e.propertyName !== 'max-height') return;
          this.#animating = false;
          this.#expanded = true;
          this.classList.remove('is-animating-expand');
          this.classList.add('is-expanded');
          this.#content.style.maxHeight = 'none';
          this.#content.style.overflow = 'visible';
          this.#toggle.setAttribute('aria-expanded', 'true');
          this.#label.textContent = this.#toggle.dataset.labelLess || '';
        },
        { once: true }
      );
    }

    #animateCollapse() {
      if (!this.#content || !this.#needsClamp) return;

      const collapsed = this.#collapsedHeightPx();
      const full = this.#content.scrollHeight;

      this.#animating = true;
      this.classList.remove('is-expanded');

      this.#content.style.overflow = 'hidden';
      this.#content.style.maxHeight = `${full}px`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#content.style.maxHeight = `${collapsed}px`;
        });
      });

      this.#content.addEventListener(
        'transitionend',
        (e) => {
          if (e.target !== this.#content || e.propertyName !== 'max-height') return;
          this.#animating = false;
          this.#expanded = false;
          this.#toggle.setAttribute('aria-expanded', 'false');
          this.#label.textContent = this.#toggle.dataset.labelMore || '';
        },
        { once: true }
      );
    }
  }

  customElements.define('byte-collection-description', ByteCollectionDescription);
}
